<template>
  <div v-if="shouldShowShutdownDetails" class="auto-shutdown-details">
    <div class="auto-shutdown-details__row">
      <v-icon>{{ iconBasedOnFeature }}</v-icon>

      <div class="auto-shutdown-details__row__item">
        <div class="auto-shutdown-details__title">{{ t(`Title.${usingType}`) }}</div>

        <schedule-label v-if="is(ScheduleType.DATE)" :is-online="isOnline" />

        <uptime-label v-else-if="is(ScheduleType.UPTIME)" :is-online="isOnline" :shutdown="shutdown" />
      </div>

      <div v-if="isOnline" class="auto-shutdown-details__control">
        <v-menu left :nudge-bottom="34">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" large icon>
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list class="otherOptionsList">
            <v-list-item @click="$emit('shutdown:postpone')">
              <v-list-item-title>
                <div class="optionItem">
                  <v-icon left>action_delay</v-icon>
                  <span>{{ t("Menu.postpone") }}</span>
                </div>
              </v-list-item-title>
            </v-list-item>

            <v-list-item @click="$emit('shutdown:remove')">
              <v-list-item-title>
                <div class="optionItem">
                  <span>{{ t("Menu.remove") }}</span>
                </div>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { WorkspaceOnlineStatusEnum, type WorkspaceTemplate } from "@/types/workspace"
import { useStore } from "@/composable/useStore"
import { defineComponent, computed } from "@vue/composition-api"
import { ScheduledOnOff, ShutdownSettings } from "@/views/appLauncher/lib/types"
import { createTranslationModule } from "@/plugins/i18n"
import ScheduleLabel from "./_ScheduleLabel.vue"
import UptimeLabel from "./_UptimeLabel.vue"

enum ScheduleType {
  DATE = "date",
  UPTIME = "uptime"
}

export default defineComponent({
  name: "AutoShutdownDetails",
  setup() {
    const { spaceModule } = useStore()
    const t = createTranslationModule("WorkspaceEdit.Header.AutoShutdownDetails")

    const space = computed<WorkspaceTemplate>(() => {
      return spaceModule.watching
    })

    const isOnline = computed(() => {
      return space.value?.onlineStatus === WorkspaceOnlineStatusEnum.Online
    })

    const timerRunning = computed(() => {
      const space = spaceModule.watching
      const hasShutdownDefinition = Boolean(space.autoShutdownDefinition)

      return hasShutdownDefinition
    })

    const schedule = computed<ScheduledOnOff>(() => {
      return spaceModule.scheduleModule.scheduledOnOff
    })

    const shutdown = computed<ShutdownSettings>(() => {
      return spaceModule.scheduleModule.shutdownSettings
    })

    const shouldShowShutdownDetails = computed(() => {
      if (isOnline.value) {
        return timerRunning.value
      } else {
        return is(ScheduleType.DATE) || is(ScheduleType.UPTIME)
      }
    })

    const usingType = computed(() => {
      return is(ScheduleType.DATE) ? ScheduleType.DATE : ScheduleType.UPTIME
    })

    const iconBasedOnFeature = computed(() => {
      const shutdownIcon = "action_delay"
      const scheduleIcon = "action_date_and_time_calendar"

      if (is(ScheduleType.DATE)) {
        return scheduleIcon
      } else {
        return shutdownIcon
      }
    })

    const is = (type: ScheduleType) => {
      if (type === ScheduleType.DATE) {
        return schedule.value.scheduledOnOffEnabled
      } else if (type === ScheduleType.UPTIME) {
        return shutdown.value.autoShutdownEnabled
      }
    }

    return {
      ScheduleType,
      t,
      is,
      usingType,
      isOnline,
      iconBasedOnFeature,
      shutdown,
      shouldShowShutdownDetails
    }
  },
  components: {
    ScheduleLabel,
    UptimeLabel
  }
})
</script>
